import Controller, { OnBeforeCancel, OnBeforeSubmit, OnBlur, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { FormAPI, InputAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { FragmentNames, ScoreNrsCreateAliases } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { Inject } from '@odinjs/odin';
import { Endpoint } from '../../../../constants/endpoints';
import DialogService from '../../../../services/dialog-service';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';
import { LocatorTypes } from '../../../../controllers/locator/locator-controller';
import timeService from '../../../../services/time-service';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { DomainType } from '../../score-types';
import { Value } from '@philips-internal/oswald-ui-core';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import calculateImcReturned from '../../../../utils/score-nrs-imc';

interface ResponseCreateScoreNRS {
  age?: number;
  diseaseSeverity?: number;
  dtEvaluation?: string;
  dtInactivation?: string;
  dtRelease?: string;
  dtUpdate?: string;
  dtUpdateNrec?: string;
  elementNumber?: number;
  height?: number;
  hour?: number;
  idScoreIndexes?: number;
  illnessUti?: string;
  imc?: number;
  justification?: string;
  levelAttention?: string;
  note?: string;
  nrAttendance?: number;
  nrSignature?: number;
  nrSignatureInactivation?: number;
  nutritionalStatus?: number;
  professionalCode?: string;
  punctuation?: number;
  reducedFeeding?: string;
  situation?: string;
  user?: string;
  userInactivation?: string;
  userNrec?: string;
  usualWeight?: number;
  weight?: number;
  weightLoss?: number;
  weightLossLastMonth?: string;
}

@Controller({component: ScoreNrsCreateAliases.SCORE_NRS_ADD_LAYOUT, fragment: FragmentNames.SCORE_NRS_CREATE})
export default class ScoreNrsCreateController extends BaseController {

  @Inject(ScoreNrsCreateAliases.FORM_SCORE_NRS_CREATE)
  private formAPI!: FormAPI;

  @Inject(ScoreNrsCreateAliases.INPUT_EVALUATION_DATE)
  private evaluationDateInputAPI!: InputAPI;

  @Inject(ScoreNrsCreateAliases.INPUT_AGE)
  private ageInputAPI!: InputAPI;

  @Inject(ScoreNrsCreateAliases.INPUT_WEIGHT)
  private weightInputAPI!: InputAPI;

  @Inject(ScoreNrsCreateAliases.INPUT_HEIGHT)
  private heightInputAPI!: InputAPI;

  @Inject(ScoreNrsCreateAliases.INPUT_IMC)
  private imcInputAPI!: InputAPI;

  @Inject(ScoreNrsCreateAliases.INPUT_USUAL_WEIGHT)
  private usualWeightInputAPI!: InputAPI;

  @Inject(ScoreNrsCreateAliases.INPUT_WEIGHT_LOSS)
  private weightLossInputAPI!: InputAPI;

  private calculateWeightLoss(): void {
    const weight = Number(this.weightInputAPI.getValue());
    const usualWeight = Number(this.usualWeightInputAPI.getValue());

    if(!weight || !usualWeight) {
      this.weightLossInputAPI.setValue(0);
      return
    }
    if(Number.isFinite(weight) || Number.isFinite(usualWeight)){
      const weightLoss = (((usualWeight - weight) * 100) / usualWeight)
      this.weightLossInputAPI.setValue(weightLoss.toFixed(2))
    }
  }

  @OnMount({ target: ScoreNrsCreateAliases.FORM_SCORE_NRS_CREATE })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_NRS
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
    timeService.getCurrentDateTime()
      .then(date => this.evaluationDateInputAPI.setValue(date))
      .catch(() => this.evaluationDateInputAPI.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
    this.ageInputAPI.setValue(InformationBarUtils.age);
  }

  @OnBlur(ScoreNrsCreateAliases.INPUT_WEIGHT)
  handleIMCW(): void {
    this.calculateIMC();
    this.calculateWeightLoss();
  }

  @OnBlur(ScoreNrsCreateAliases.INPUT_USUAL_WEIGHT)
  handleWeightLossU(): void {
    this.calculateWeightLoss();
  }

  @OnBlur(ScoreNrsCreateAliases.INPUT_HEIGHT)
  handleIMCH(): void {
    this.calculateIMC();
  }
  @OnBeforeSubmit({ target: ScoreNrsCreateAliases.FORM_SCORE_NRS_CREATE })
  handleBeforeSubmit(): void {
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    const isCreated = this.context.get('isCreated');
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formData = this.formAPI.getRecord();
    const dtEvaluation = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);
    const age = formData.age || '';
    const weight = formData.weight || '';
    const height = formData.height || '';
    const imc = formData.imc || '';
    const usualWeight = formData.usualWeight || '';
    const weightLoss = formData.weightLoss || '';
    const nutritionalStatus = formData.nutritionalStatusDomain as DomainType;
    const diseaseSeverity = formData.diseaseSeverityDomain as DomainType;
    const note = formData.note || '';
    const illnessUti = BooleanUtils.isPositiveValue(formData.uti as boolean)
    const reducedFeeding = BooleanUtils.isPositiveValue(formData.reducedFeeding as boolean)
    const weightLossLastMonth = BooleanUtils.isPositiveValue(formData.weightLossLastMonth as boolean)
    const body = {
      age,
      diseaseSeverity: diseaseSeverity.valueDomain,
      dtEvaluation,
      height,
      idScoreIndexes: null,
      illnessUti,
      imc,
      note,
      nrAttendance: encounterId as number,
      nutritionalStatus: nutritionalStatus.valueDomain,
      professionalCode: user?.physicalPersonId,
      reducedFeeding,
      usualWeight,
      weight,
      weightLoss,
      weightLossLastMonth,
    }
    this.createNewNrsScore(body);
  }

  @OnBeforeCancel(ScoreNrsCreateAliases.FORM_SCORE_NRS_CREATE)
  handleCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }

  private createNewNrsScore(body: Record<string, Value>): void {
    this.formAPI.setLoading(true);
    HttpService.post(Endpoint.POST_CREATE_SCORE_NRS, undefined, {body})
      .then(response => {
        const {idScoreIndexes} = response.body as ResponseCreateScoreNRS;
        this.showModalSubmit(idScoreIndexes as number);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formAPI.setLoading(false));
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseNrsScore(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }
  private releaseNrsScore(sequenceNumber: number): void {
    this.formAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_SCORE_NRS, { sequenceNumber })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formAPI.setLoading(false)
      });
  }

  private calculateIMC(): void {
    const weight = Number(this.weightInputAPI.getValue());
    const height = Number(this.heightInputAPI.getValue());
    let imc = 0;
    if(Number.isFinite(weight) || Number.isFinite(height)){
      imc = calculateImcReturned(weight,height);
    }
    this.imcInputAPI.setValue(imc)
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => { this.goBack(); },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  @OnUnmount(ScoreNrsCreateAliases.SCORE_NRS_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_NRS;
  }
}
